import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowUp, FaArrowDown, FaChartBar, FaPercentage,
  FaBell, FaFileInvoice, FaExclamationTriangle, FaArrowRight
} from 'react-icons/fa';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const hoy = new Date();
const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
const MESES_CORTO = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const fechaTexto = hoy.toLocaleDateString('es-PE', { weekday:'long', day:'2-digit', month:'long', year:'numeric' });
const mesActual  = MESES[hoy.getMonth()] + ' de ' + hoy.getFullYear();
const COLORS_PIE = ['#FF6B00','#001B4F','#22C55E','#3B82F6','#8B5CF6','#F59E0B'];

export default function DashboardPage() {
  const { usuario } = useAuth();
  const navigate    = useNavigate();
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const { data } = await api.get('/dashboard');
        setDatos(data.data);
      } catch {
        setDatos(null);
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  if (cargando) return (
    <Layout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
        <div className="spinner-custom" />
      </div>
    </Layout>
  );

  const ingresos = parseFloat(datos?.consolidado?.total_ingresos || 0);
  const gastos   = parseFloat(datos?.consolidado?.total_gastos   || 0);
  const saldo    = parseFloat(datos?.consolidado?.saldo_neto     || 0);
  const tasa     = ingresos > 0 ? (gastos / ingresos * 100) : 0;
  const fmt = (n) => parseFloat(n).toLocaleString('es-PE', { minimumFractionDigits:2 });

  const kpis = [
    { label:'Total Ingresos', valor:`S/ ${fmt(ingresos)}`, icono:<FaArrowUp size={18} color="#22C55E" />, iconBg:'rgba(34,197,94,0.1)', trend:'+0% este mes', trendColor:'#22C55E' },
    { label:'Total Gastos',   valor:`S/ ${fmt(gastos)}`,   icono:<FaArrowDown size={18} color="#EF4444" />, iconBg:'rgba(239,68,68,0.1)', trend:'+0% este mes', trendColor:'#EF4444' },
    { label:'Saldo Neto',     valor:`S/ ${fmt(saldo)}`,    icono:<FaChartBar size={18} color="#3B82F6" />, iconBg:'rgba(59,130,246,0.1)', trend:'Balance actual', trendColor:'#3B82F6' },
    { label:'Tasa de Gasto',  valor:`${tasa.toFixed(1)} %`,icono:<FaPercentage size={18} color="#8B5CF6" />, iconBg:'rgba(139,92,246,0.1)', trend:'vs ingresos', trendColor:'#8B5CF6' },
  ];

  const graficoData  = datos?.ingresos_gastos_mensual || [];
  const categorias   = datos?.gastos_por_categoria    || [];
  const alertas      = datos?.alertas                 || [];
  const comprobantes = datos?.ultimos_comprobantes    || [];

  return (
    <Layout>
      {/* Encabezado */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h4 style={{ fontSize:20, fontWeight:700, color:'#001B4F', marginBottom:2, letterSpacing:'-0.3px' }}>
            Dashboard
          </h4>
          <p style={{ fontSize:13, color:'#64748B', margin:0 }}>
            Bienvenido, <strong style={{ color:'#001B4F' }}>{usuario?.nombre}</strong> — Resumen de {mesActual}
          </p>
        </div>
        <div style={{
          background:'#fff', border:'1.5px solid #E2E8F0', borderRadius:10,
          padding:'7px 14px', fontSize:12, color:'#64748B', fontWeight:500,
          boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
        }}>
          📅 {fechaTexto}
        </div>
      </div>

      {/* KPI Cards — más compactas */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{
            background:'#fff', borderRadius:16, padding:'16px 18px',
            boxShadow:'0 2px 12px rgba(0,27,79,0.06)',
            border:'1px solid #E2E8F0', transition:'all .25s', cursor:'default',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,27,79,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,27,79,0.06)'; }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:10, fontWeight:700, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'0.8px' }}>
                {k.label}
              </span>
              <div style={{ width:34, height:34, borderRadius:10, background:k.iconBg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {k.icono}
              </div>
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:'#1E293B', letterSpacing:'-0.5px', marginBottom:6 }}>
              {k.valor}
            </div>
            <div style={{ fontSize:11, color:k.trendColor, fontWeight:600 }}>{k.trend}</div>
          </div>
        ))}
      </div>

      {/* Gráficos */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:20 }}>
        <div style={{ background:'#fff', borderRadius:16, padding:'20px 24px', boxShadow:'0 2px 12px rgba(0,27,79,0.06)', border:'1px solid #E2E8F0' }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#001B4F' }}>Ingresos vs Gastos</div>
            <div style={{ fontSize:11, color:'#94A3B8' }}>Últimos 12 meses</div>
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={graficoData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="mes" tick={{ fontSize:10, fill:'#94A3B8' }} />
              <YAxis tick={{ fontSize:10, fill:'#94A3B8' }} />
              <Tooltip contentStyle={{ borderRadius:10, border:'1px solid #E2E8F0', fontSize:12 }} formatter={(v) => [`S/ ${fmt(v)}`, '']} />
              <Line type="monotone" dataKey="ingresos" stroke="#22C55E" strokeWidth={3} dot={{ r:5, fill:'#22C55E' }} name="Ingresos" />
              <Line type="monotone" dataKey="gastos"   stroke="#EF4444" strokeWidth={3} dot={{ r:5, fill:'#EF4444' }} name="Gastos" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background:'#fff', borderRadius:16, padding:'20px 24px', boxShadow:'0 2px 12px rgba(0,27,79,0.06)', border:'1px solid #E2E8F0' }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#001B4F' }}>Gastos por Categoría</div>
            <div style={{ fontSize:11, color:'#94A3B8' }}>Distribución actual</div>
          </div>
          {categorias.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={categorias} dataKey="total" nameKey="categoria" cx="50%" cy="50%" outerRadius={70}>
                  {categorias.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `S/ ${fmt(v)}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:190, display:'flex', alignItems:'center', justifyContent:'center', color:'#CBD5E1', flexDirection:'column', gap:8 }}>
              <FaChartBar size={28} />
              <span style={{ fontSize:12 }}>Sin categorías</span>
            </div>
          )}
        </div>
      </div>

      {/* Actividad y Alertas */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
        <div style={{ background:'#fff', borderRadius:16, padding:'20px 24px', boxShadow:'0 2px 12px rgba(0,27,79,0.06)', border:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#001B4F', marginBottom:14 }}>Actividad reciente</div>
          {comprobantes.length > 0 ? (
            <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0 }}>
              <thead>
                <tr>
                  {['Tipo','Serie/N°','Monto','Fecha','Estado'].map((h,i) => (
                    <th key={i} style={{
                      background:'#001B4F', color:'rgba(255,255,255,0.8)',
                      fontSize:10, fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'0.8px', padding:'9px 12px', border:'none',
                      borderRadius: i===0?'8px 0 0 0':i===4?'0 8px 0 0':0,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comprobantes.slice(0,5).map((c, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #F1F5F9' }}>
                    <td style={{ padding:'10px 12px', fontSize:12 }}><span className="badge-pendiente">{c.tipo || '—'}</span></td>
                    <td style={{ padding:'10px 12px', fontSize:12, color:'#64748B' }}>{c.serie}-{c.numero_comprobante || '—'}</td>
                    <td style={{ padding:'10px 12px', fontSize:12, fontWeight:600 }}>S/ {fmt(c.monto_total || 0)}</td>
                    <td style={{ padding:'10px 12px', fontSize:12, color:'#94A3B8' }}>{c.fecha_emision?.split('T')[0] || '—'}</td>
                    <td style={{ padding:'10px 12px' }}><span className={`badge-${c.estado || 'activo'}`}>{c.estado || 'activo'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign:'center', padding:'28px 0', color:'#CBD5E1' }}>
              <FaFileInvoice size={28} style={{ marginBottom:8 }} />
              <div style={{ fontSize:12 }}>Sin actividad reciente</div>
            </div>
          )}
        </div>

        <div style={{ background:'#fff', borderRadius:16, padding:'20px 24px', boxShadow:'0 2px 12px rgba(0,27,79,0.06)', border:'1px solid #E2E8F0' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FaBell size={14} color="#FF6B00" />
              <span style={{ fontSize:14, fontWeight:700, color:'#001B4F' }}>Alertas recientes</span>
            </div>
            {alertas.length > 3 && (
              <button onClick={() => navigate('/alertas')} style={{
                background:'none', border:'none', color:'#FF6B00',
                fontSize:11, fontWeight:600, cursor:'pointer',
                display:'flex', alignItems:'center', gap:4,
              }}>
                Ver todas <FaArrowRight size={9} />
              </button>
            )}
          </div>
          {alertas.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {alertas.slice(0,3).map((a, i) => (
                <div key={i} style={{
                  padding:'10px 12px', borderRadius:10,
                  background: a.tipo_alerta==='critica'?'#FEE2E2':a.tipo_alerta==='advertencia'?'#FEF3C7':'#DBEAFE',
                  border:`1px solid ${a.tipo_alerta==='critica'?'#FECACA':a.tipo_alerta==='advertencia'?'#FDE68A':'#BFDBFE'}`,
                  display:'flex', alignItems:'flex-start', gap:8,
                }}>
                  <FaExclamationTriangle size={11} color={a.tipo_alerta==='critica'?'#EF4444':a.tipo_alerta==='advertencia'?'#D97706':'#3B82F6'} style={{ marginTop:2, flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, color:'#1E293B', marginBottom:1 }}>{a.titulo || 'Alerta'}</div>
                    <div style={{ fontSize:10, color:'#64748B', lineHeight:1.4 }}>{a.mensaje || ''}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'28px 0', color:'#CBD5E1' }}>
              <FaBell size={28} style={{ marginBottom:8 }} />
              <div style={{ fontSize:12 }}>Sin alertas pendientes</div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}